import { AxiosError } from 'axios';
import { JSONSchema, JSONSchemaProp, TError } from 'models';
import { translate } from './translate';
import { DEFAULT_ERROR_MESSAGE } from '../config/api';
import { cloneObject } from './cloneObject';
import { getAllObjectValuesByKey } from './getAllObjectValuesByKey';
import { EXTERNAL_IDD_URL_ERROR_CODE, IDDExternalLink } from 'app/Components/IDD';

const VEHICLE_DB_MESSAGE_OVERRIDES: Record<string, string> = {
  'Mapping not found for custom vehicle make with id: 0': 'error.make_id_missing',
};

const ERROR_KEYS_TO_FILTER = [
  '(root)',
  'reasons',
  'worker',
];

const CUSTOM_CODE_ERROR_KEYS = [
  EXTERNAL_IDD_URL_ERROR_CODE
];

const ISSUING_ERROR_KEYS_TO_FILTER = [
  ...ERROR_KEYS_TO_FILTER,
  'log_rec_id',
  'offer_id',
];

const ERROR_MESSAGES_TO_FILTER = [
  'Must validate "then" as "if" was valid',
];

const ERROR_CODES_TO_IGNORE_PARAMS = [
  'QMT00043',
  'QMT00064',
];

const CEPIK_ERROR_CODE_PREFIX = 'CPK';

type TQuoteAPIError = {
  code: string,
  message: string,
  params: Record<string, never>,
};

export const processAPIError = (error: AxiosError, array?: boolean, hideMessages?: boolean) => {
  let errorData: string | TError[];

  if (array) {
    errorData = setDefaultErrorsArray();
  } else {
    errorData = translate({ key: DEFAULT_ERROR_MESSAGE });
  }

  if (error.response) {
    let data: any = error.response.data;

    if (typeof data === 'string') {
      try {
        data = data ? JSON.parse(data) : undefined;
      } catch {
        data = undefined;
      }
    }

    if (data?.errors) {
      const filteredErrors = data.errors.filter((error: { code: string, message: string }) => !ERROR_KEYS_TO_FILTER.includes(error.code));

      if (array) {
        errorData = filteredErrors.map((item: { code: string, message: string }) => {
          let customError = checkIfCustomError(item, true);
          if (customError) {
            return customError;
          }

          const errorCodeTranslated = translate({ key: `error.${item.code}`, fallback: item.code });

          return {
            code: hideMessages ? errorCodeTranslated : item.code,
            message: errorCodeTranslated === item.code ? item.message : hideMessages ? '' : errorCodeTranslated
          };
        });
      } else {
        const errors = filteredErrors.map((item: { code: string, message: string }) => {
          let customError = checkIfCustomError(item);
          if (customError) {
            return customError;
          }

          const errorCodeTranslated = translate({ key: `error.${item.code}`, fallback: item.code });

          return hideMessages ? `${errorCodeTranslated}` : `${item.code}: ${errorCodeTranslated === item.code ? item.message : errorCodeTranslated}`;
        });
        errorData = errors.join('/n');
      }
    }
  }

  return errorData;
};

export const processAPIQuoteError = (error: AxiosError, array: boolean = false) => {
  let errorData: string | TError[];

  if (array) {
    errorData = setDefaultErrorsArray();
  } else {
    errorData = translate({ key: DEFAULT_ERROR_MESSAGE });
  }

  if (error.response) {

    let data: any = error.response.data;

    if (typeof data === 'string') {
      try {
        data = data ? JSON.parse(data) : undefined;
      } catch {
        data = undefined;
      }
    }

    if (data?.response?.errors) {
      errorData = processErrors(data?.response?.errors, errorData, array);
    } else if (data?.errors) {
      errorData = processErrors(data?.errors, errorData, array);
    }
  }

  return errorData;
};

export const processAPICalculationError = (error: AxiosError, array: boolean = false, hideMessages?: boolean) => {
  let errorData: string | TError[];

  if (array) {
    errorData = setDefaultErrorsArray();
  } else {
    errorData = translate({ key: DEFAULT_ERROR_MESSAGE });
  }

  if (error.response) {
    const data: any = error.response.data;
    if (data?.response?.errors) {
      errorData = processCalculationErrors(data?.response?.errors, errorData, array, hideMessages);
    } else if (data?.errors) {
      errorData = processCalculationErrors(data?.errors, errorData, array, hideMessages);
    }
  }

  return errorData;
};

export const processVehicleDBErrors = (error: AxiosError, array: boolean = false) => {
  let errorData: string | TError[];

  if (array) {
    errorData = setDefaultErrorsArray();
  } else {
    errorData = translate({ key: DEFAULT_ERROR_MESSAGE });
  }

  if (error.response) {
    const data: any = error.response.data;
    if (data.result) {
      const overrideKey = VEHICLE_DB_MESSAGE_OVERRIDES[data.result.message];

      const result = overrideKey
        ? { ...data.result, message: translate({ key: overrideKey }) }
        : data.result;

      errorData = processErrors([result], errorData, array);
    }
  }

  return errorData;

};

const isCepikErrorCode = (code: string) => code?.startsWith(CEPIK_ERROR_CODE_PREFIX);

const getVisibleErrorKeys = (errors: Record<string, string>) => Object.keys(errors)
  .filter((error: string) => !ISSUING_ERROR_KEYS_TO_FILTER.includes(error) && !ERROR_MESSAGES_TO_FILTER.includes(errors[error]));

const processErrors = (errors: TQuoteAPIError[], errorData: string | TError[], array: boolean) => {
  if (array) {
    errorData = [];
    errors.forEach((item: TQuoteAPIError) => {
      errorData = [
        ...errorData as TError[],
        {
          code: item.code,
          message: translate({ key: `error.${item.code}`, fallback: item.message })
        }
      ];

      if (isCepikErrorCode(item.code)) {
        // CEPIK field-level sub-errors are surfaced as input highlights by
        // applyCepikFieldErrors in Cepik/index.tsx — skip here to avoid
        // showing the same problem twice (once as text, once as highlight).
        return;
      }

      if (item.params?.errors) {
        Object.keys(item.params.errors).filter((error: string) => !ERROR_KEYS_TO_FILTER.includes(error)).forEach(subItem => {
          errorData = [
            ...errorData as TError[],
            {
              code: subItem,
              message: translate({ key: `error.${subItem}`, fallback: item.params.errors[subItem] })
            }
          ];
        });
      } else if (item.params?.error) {
        errorData = [
          ...errorData as TError[],
          {
            code: '',
            message: item.params.error
          }
        ];
      } else if (item.params && Object.keys(item.params)?.length) {
        const errorList = Object.keys(item.params).filter((error: string) => !ERROR_KEYS_TO_FILTER.includes(error)).map(error => ({
          code: error,
          message: translate({ key: `error.${error}`, fallback: item.params[error] })
        }));
        if (item.params.reasons) {
          Object.keys(item.params.reasons).forEach(reason => {
            errorList.push(
              {
                code: reason,
                message: translate({ key: `error.${reason}`, fallback: item.params.reasons[reason] })
              }
            );
          });
        }
        errorData = [
          ...errorData as TError[],
          ...errorList
        ];
      }
    });
  } else {
    const errorsArray: string[] = [];
    errors.forEach((item: TQuoteAPIError) => {
      errorsArray.push(`${item.code ? `${item.code}:` : ''} ${(item.code ? translate({ key: `error.${item.code}`, fallback: item.message }) : item.message) || ''}`);

      if (isCepikErrorCode(item.code)) {
        // CEPIK field-level sub-errors are surfaced as input highlights by
        // applyCepikFieldErrors in Cepik/index.tsx — skip here to avoid
        // showing the same problem twice (once as text, once as highlight).
        return;
      }

      if (item.params?.errors) {
        Object.keys(item.params.errors).filter((error: string) => !ERROR_KEYS_TO_FILTER.includes(error)).forEach(subItem => {
          errorsArray.push(`${subItem ? `${subItem}:` : ''} ${subItem ? translate({ key: `error.${subItem}`, fallback: item.params.errors[subItem] }) : item.params.errors[subItem]}`);
        });
      } else if (item.params?.error) {
        errorsArray.push(item.params.error);
      }
    });
    errorData = errorsArray.join(' \n ');
  }
  return errorData;
};

// The specific errors an insurer returned for a failed request. They replace the
// error's own message, which is only a generic wrapper around them.
const getCalculationSubErrors = (item: TQuoteAPIError, hideMessages?: boolean): TError[] => {
  if (item.params?.errors) {
    return getVisibleErrorKeys(item.params.errors).map(subItem => {
      const errorCodeTranslated = translate({ key: `error.${subItem}`, fallback: subItem });

      return {
        code: errorCodeTranslated,
        message: (hideMessages && errorCodeTranslated === subItem) || !hideMessages ? item.params.errors[subItem] : ''
      };
    });
  }

  if (item.params?.error) {
    return [{
      code: '',
      message: item.params.error
    }];
  }

  // `reasons` is the insurer's list of why the request failed — specific errors,
  // not context, so they replace the error's own generic message.
  if (Object.keys(item.params?.reasons || {}).length) {
    return Object.keys(item.params.reasons).map(reason => {
      const errorCodeTranslated = translate({ key: `error.${reason}`, fallback: reason });

      return {
        code: errorCodeTranslated,
        message: (hideMessages && errorCodeTranslated === reason) || !hideMessages ? item.params.reasons[reason] : ''
      };
    });
  }

  return [];
};

// Contextual params (policy numbers and the like) that sit next to the errors.
// `reasons` itself is filtered out by getVisibleErrorKeys.
const getCalculationErrorParams = (item: TQuoteAPIError, hideMessages?: boolean): TError[] => {
  // These two carry the errors themselves rather than context, and their siblings
  // were never rendered — getCalculationSubErrors owns them entirely.
  if (!item.params || item.params.errors || item.params.error || !Object.keys(item.params).length) {
    return [];
  }

  const errorList = getVisibleErrorKeys(item.params).map(error => ({
    code: translate({ key: `error.${error}`, fallback: error }),
    message: (hideMessages && translate({ key: `error.${error}`, fallback: error }) === error) || !hideMessages ? item.params[error] : ''
  }));

  return errorList;
};

const processCalculationErrors = (errors: TQuoteAPIError[], errorData: string | TError[], array: boolean, hideMessages?: boolean) => {
  if (array) {
    errorData = [];
    errors.forEach((item: TQuoteAPIError) => {
      const errorCodeTranslated = translate({ key: `error.${item.code}`, fallback: item.code });
      const errorMessage = {
        code: errorCodeTranslated,
        message: (hideMessages && errorCodeTranslated === item.code) || !hideMessages ? item.message : ''
      };

      // These codes carry interpolation data rather than errors, so the message
      // is all there is to show — checked before anything reads their params.
      if (ERROR_CODES_TO_IGNORE_PARAMS.includes(item.code)) {
        errorData = [
          ...errorData as TError[],
          errorMessage
        ];
        return;
      }

      const subErrors = getCalculationSubErrors(item, hideMessages);

      errorData = [
        ...errorData as TError[],
        // The error's own message is a generic wrapper around the specific
        // errors — show it only when there are none.
        ...(subErrors.length ? [] : [errorMessage]),
        ...getCalculationErrorParams(item, hideMessages),
        ...subErrors
      ];
    });
  } else {
    const errorsArray: string[] = [];
    errors.forEach((item: TQuoteAPIError) => {
      const subErrors = ERROR_CODES_TO_IGNORE_PARAMS.includes(item.code) ? [] : getCalculationSubErrors(item, hideMessages);

      if (subErrors.length) {
        errorsArray.push(...subErrors.map(subError => `${subError.code ? `${subError.code}:` : ''} ${subError.message}`));
        return;
      }

      errorsArray.push(`${item.code ? `${translate({ key: `error.${item.code}`, fallback: item.code })}:` : ''} ${item.message || ''}`);
    });
    errorData = errorsArray.join(' \n ');
  }
  return errorData;
};

export const processCEIDGErrors = (error: AxiosError) => {
  if (error.response?.status === 404) {
    return translate({ key: 'ceidg.error.not_found' });
  }

  const data = error.response?.data as any;
  if (data?.message) {
    return data.message;
  }

  return translate({ key: 'error.common' });
};

export const processIssuingErrors = (error: AxiosError, schema: JSONSchemaProp | null) => {
  let errorData: TError[] = [];

  if (error.response) {

    let data: any = error.response.data;

    if (typeof data === 'string') {
      try {
        data = data ? JSON.parse(data) : undefined;
      } catch {
        data = undefined;
      }
    }

    const errors = data?.response?.errors || data?.errors;

    if (errors) {
      errors.forEach((item: TQuoteAPIError) => {

        const errorCountBefore = errorData.length;
        let hasValidationErrors = false;

        //Validation errors
        if (item.code === 'QMT00015' && schema) {
          const schemaValidationErrorData = processValidationErrors(item, schema);

          // An empty array means every validation error was filtered out as
          // noise — fall through rather than swallowing the failure.
          if (schemaValidationErrorData?.length) {
            hasValidationErrors = true;
            errorData = [
              ...errorData as TError[],
              ...schemaValidationErrorData
            ];
          }
        }

        // These codes carry interpolation data rather than errors, so their params
        // are skipped and the fallback below renders the message on its own.
        if (ERROR_CODES_TO_IGNORE_PARAMS.includes(item.code)) {
          hasValidationErrors = true;
        }

        if (!hasValidationErrors) {
          if (item.params?.errors) {
            getVisibleErrorKeys(item.params.errors).forEach(subItem => {
              errorData = [
                ...errorData as TError[],
                {
                  code: '',
                  message: translate({ key: `error.${subItem}`, fallback: item.params.errors[subItem] })
                }
              ];
            });
          } else if (item.params && Object.keys(item.params)?.length) {
            const errorList = getVisibleErrorKeys(item.params).map(error => ({
              code: '',
              message: translate({ key: `error.${error}`, fallback: item.params[error] })
            }));
            if (item.params.reasons) {
              Object.keys(item.params.reasons).forEach(reason => {
                errorList.push(
                  {
                    code: '',
                    message: translate({ key: `error.${reason}`, fallback: item.params.reasons[reason] })
                  }
                );
              });
            }
            errorData = [
              ...errorData as TError[],
              ...errorList
            ];
          }
        }

        // Nothing survived filtering (or the error carried no params at all) —
        // fall back to the error's own message so the screen is never blank.
        if (errorData.length === errorCountBefore) {
          errorData = [
            ...errorData as TError[],
            {
              code: '',
              message: translate({ key: `error.${item.code}`, fallback: item.message })
            }
          ];
        }

      });
    }

  }

  return errorData;
};

const processValidationErrors = (item: TQuoteAPIError, schema: JSONSchemaProp) => {
  const schemaProperties = schema.properties;
  let errorData: TError[] = [];

  if (schemaProperties && item.params?.errors) {
    getVisibleErrorKeys(item.params.errors).forEach(subItem => {

      errorData = [
        ...errorData as TError[],
        getValidationError(subItem, item.params.errors[subItem], schemaProperties),
      ];
    });
    return errorData;
  }

};

const getValidationError = (
  key: string,
  message: string,
  additionalOptions: JSONSchema,
): TError => {
  let translationKey = getTranslationKeyFromSchema(key, additionalOptions) || key;
  let errorMessage: string;

  if (message.includes('Invalid type')) {
    errorMessage = translate({
      key: 'issuing.error.wrong_type',
      replace: [translate({ key: translationKey, fallback: key })],
    });
  } else if (message.includes('is required')) {
    errorMessage = translate({
      key: 'issuing.error.is_required',
      replace: [translate({ key: translationKey, fallback: key })],
    });
  } else {
    errorMessage = translate({ key: `error.${key}`, fallback: message });
  }

  return {
    code: '',
    message: errorMessage,
  };
};

const getTranslationKeyFromSchema = (key: string, additionalOptions: JSONSchema) => {
  const splitObjectPath = key.split('.');
  let objectWithTranslation = cloneObject(additionalOptions);

  const keyToFind = splitObjectPath[splitObjectPath.length - 1];

  const objectWithTranslationKey = getAllObjectValuesByKey(objectWithTranslation, keyToFind)?.[0];

  if (objectWithTranslationKey) {
    return objectWithTranslationKey?.title_translation_key;
  }

};

const setDefaultErrorsArray = () => [{ code: '', message: translate({ key: DEFAULT_ERROR_MESSAGE }) }];


const processCustomError = (
  item: {
    params?: Record<string, any>,
    code: string, message: string,
  },
  isArray?: boolean
) => {
  switch (item.code) {
    case EXTERNAL_IDD_URL_ERROR_CODE:
      if (isArray) {
        return {
          code: item.params?.create_url ? '' : translate({ key: `error.${item.code}`, fallback: item.code }),
          message: item.params?.create_url ? <IDDExternalLink link={item.params?.create_url} /> : '',
        };
      } else {
        return item.params?.create_url ? <IDDExternalLink link={item.params?.create_url} /> : translate({ key: `error.${item.code}`, fallback: item.message });
      }

    default:
      return false;
  }
};

const checkIfCustomError = (item: {
  params?: Record<string, any>,
  code: string, message: string,
}, isArray?: boolean) => {
  if (CUSTOM_CODE_ERROR_KEYS.includes(item.code)) {
    let customError = processCustomError(item, isArray);
    if (customError) {
      return customError;
    }

    return false;
  }
};
